import React, { useState, useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import {
  Settings as SettingsIcon, Building2, Mail, Phone, MapPin,
  Globe, DollarSign, Bell, Save, Upload, Trash2, Palette,
  Clock, MessageSquare, MessageCircle, CreditCard, CalendarCheck, Lock,
  Sparkles, Zap, Link2, Copy, RefreshCw, ToggleRight, UserX,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  PageHeader, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, Separator,
} from "../ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui";
import RazorpaySettings from "./RazorpaySettings";
import BankDetailsSettings from "./BankDetailsSettings";
import ZoomSettings from "./ZoomSettings";
import ZohoMeetingSettings from "./ZohoMeetingSettings";
import MeetingProviderSelector from "./MeetingProviderSelector";
import PublicApiSettings from "./PublicApiSettings";
import ZohoSettings from "./ZohoSettings";
import LeadSourcesSettings from "./LeadSourcesSettings";
import { hasPermission, isInstituteSuperAdmin } from "../../utils/permissions";
import { useBranding } from "../../contexts/BrandingContext";
import { usePlan } from "../../contexts/PlanContext";
import { getAISettings, updateAISettings, getAIUsage } from "../../utils/selfPacedApi";
import "./settings.scss";

/* ── Toggle Switch ── */
function ToggleSwitch({ checked, onChange, id, disabled }) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={`settings-toggle ${checked ? "settings-toggle--on" : ""} ${disabled ? "settings-toggle--disabled" : ""}`}
      onClick={() => !disabled && onChange(!checked)}
    >
      <span className="settings-toggle__thumb" />
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   General Settings Tab
   ══════════════════════════════════════════════════════════════════════════════ */
function GeneralSettings({ settings, setSettings, onSave, saving, institute }) {
  const update = (field) => (e) =>
    setSettings((s) => ({ ...s, [field]: e.target.value }));

  return (
    <>
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <Building2 size={18} /> Institute Information
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">Basic information about your training institute</p>
      </AppCardHeader>
      <AppCardContent>
        <div className="settings-form-grid">
          <div className="settings-field">
            <label htmlFor="instituteName">Institute Name</label>
            <input
              id="instituteName"
              type="text"
              value={settings.instituteName || ""}
              onChange={update("instituteName")}
              placeholder="Your institute name"
            />
          </div>
          <div className="settings-field">
            <label htmlFor="instituteEmail">Email Address</label>
            <div className="settings-input-icon">
              <Mail size={16} />
              <input
                id="instituteEmail"
                type="email"
                value={settings.instituteEmail || ""}
                onChange={update("instituteEmail")}
                placeholder="info@example.com"
              />
            </div>
          </div>
          <div className="settings-field">
            <label htmlFor="institutePhone">Phone Number</label>
            <div className="settings-input-icon">
              <Phone size={16} />
              <input
                id="institutePhone"
                type="text"
                value={settings.institutePhone || ""}
                onChange={update("institutePhone")}
                placeholder="+91 9876543210"
              />
            </div>
          </div>
          <div className="settings-field">
            <label htmlFor="address">Address</label>
            <div className="settings-input-icon">
              <MapPin size={16} />
              <input
                id="address"
                type="text"
                value={settings.address || ""}
                onChange={update("address")}
                placeholder="123 Tech Park, City"
              />
            </div>
          </div>
        </div>

        <Separator />

        <div className="settings-form-grid">
          <div className="settings-field">
            <label htmlFor="timezone">Timezone</label>
            <div className="settings-input-icon">
              <Globe size={16} />
              <select
                id="timezone"
                value={settings.timezone || "Asia/Kolkata"}
                onChange={update("timezone")}
              >
                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                <option value="America/New_York">America/New York (EST)</option>
                <option value="Europe/London">Europe/London (GMT)</option>
                <option value="Asia/Dubai">Asia/Dubai (GST)</option>
              </select>
            </div>
          </div>
          <div className="settings-field">
            <label htmlFor="currency">Currency</label>
            <div className="settings-input-icon">
              <DollarSign size={16} />
              <select
                id="currency"
                value={settings.currency || "INR"}
                onChange={update("currency")}
              >
                <option value="INR">INR - Indian Rupee</option>
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
              </select>
            </div>
          </div>
          <div className="settings-field">
            <label htmlFor="workingHoursStart">Working Hours Start</label>
            <div className="settings-input-icon">
              <Clock size={16} />
              <input
                id="workingHoursStart"
                type="time"
                value={settings.workingHoursStart || "09:00"}
                onChange={update("workingHoursStart")}
              />
            </div>
          </div>
          <div className="settings-field">
            <label htmlFor="workingHoursEnd">Working Hours End</label>
            <div className="settings-input-icon">
              <Clock size={16} />
              <input
                id="workingHoursEnd"
                type="time"
                value={settings.workingHoursEnd || "18:00"}
                onChange={update("workingHoursEnd")}
              />
            </div>
          </div>
        </div>

        <div className="settings-save-row">
          <AppButton onClick={() => onSave("general")} disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>

    {/* Portal URL — read-only info card */}
    {institute?.portalUrl && (
      <AppCard style={{ marginTop: 16 }}>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Globe size={18} /> Your Portal
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">Share this link with your students and instructors</p>
        </AppCardHeader>
        <AppCardContent>
          <div className="settings-form-grid">
            <div className="settings-field">
              <label>Portal URL</label>
              <div className="settings-input-icon" style={{ position: 'relative' }}>
                <Globe size={16} />
                <input
                  type="text"
                  value={institute.portalUrl}
                  readOnly
                  onClick={(e) => e.target.select()}
                  style={{ cursor: 'pointer', background: '#f1f5f9' }}
                />
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(institute.portalUrl)}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '0.75rem',
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
            {institute.instituteCode && (
              <div className="settings-field">
                <label>Institute Code</label>
                <div className="settings-input-icon">
                  <Building2 size={16} />
                  <input
                    type="text"
                    value={institute.instituteCode}
                    readOnly
                    style={{ background: '#f1f5f9' }}
                  />
                </div>
              </div>
            )}
          </div>
        </AppCardContent>
      </AppCard>
    )}

    {/* ── Enquiry Ownership / Visibility (Super Admin tier only) ── */}
    {isInstituteSuperAdmin() && (
      <AppCard style={{ marginTop: 16 }}>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Lock size={18} /> Enquiry Ownership Control
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Restrict enquiry visibility to assigned admins (CRM-style ownership).
            Only visible to Super Admins.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="settings-form-grid">
            <div className="settings-field settings-field--full">
              <div className="settings-toggle-row">
                <div>
                  <label>Restrict Enquiries to Assigned Admin</label>
                  <p className="settings-field-hint">
                    When enabled, regular admins (Admin / Moderator) can only see
                    enquiries explicitly assigned to them. Unassigned enquiries
                    and enquiries owned by other admins remain hidden — only
                    Super Admins can see and assign them.
                  </p>
                </div>
                <ToggleSwitch
                  id="restrictEnquiryVisibility"
                  checked={!!settings.restrictEnquiryVisibility}
                  onChange={(val) => setSettings((s) => ({ ...s, restrictEnquiryVisibility: val }))}
                />
              </div>
            </div>
          </div>
          <div className="settings-save-row">
            <AppButton onClick={() => onSave("general")} disabled={saving}>
              <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>
    )}

    {/* ── Student Auto-Deactivation ── */}
    <AppCard style={{ marginTop: 16 }}>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <UserX size={18} /> Student Auto-Deactivation
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">
          Automatically deactivate students who have no active courses after a defined period
        </p>
      </AppCardHeader>
      <AppCardContent>
        <div className="settings-form-grid">
          <div className="settings-field settings-field--full">
            <div className="settings-toggle-row">
              <div>
                <label>Enable Auto-Deactivation</label>
                <p className="settings-field-hint">
                  When enabled, students with no active batches will be automatically deactivated
                  after the configured number of days from their last course completion.
                </p>
              </div>
              <ToggleSwitch
                id="autoDeactivateEnabled"
                checked={!!settings.autoDeactivateEnabled}
                onChange={(val) => setSettings((s) => ({ ...s, autoDeactivateEnabled: val }))}
              />
            </div>
          </div>
          {settings.autoDeactivateEnabled && (
            <div className="settings-field">
              <label htmlFor="autoDeactivateDays">Deactivate After (days)</label>
              <div className="settings-input-icon">
                <Clock size={16} />
                <input
                  id="autoDeactivateDays"
                  type="number"
                  min="1"
                  max="365"
                  value={settings.autoDeactivateDays || 60}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, autoDeactivateDays: parseInt(e.target.value) || 60 }))
                  }
                  placeholder="60"
                />
              </div>
              <span className="settings-field-hint">
                Days after last course completion before student is deactivated
              </span>
            </div>
          )}
        </div>
        <div className="settings-save-row">
          <AppButton onClick={() => onSave("general")} disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Student Invite Link Settings
   ══════════════════════════════════════════════════════════════════════════════ */
function InviteSettings({ showToast }) {
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [expiryDate, setExpiryDate] = useState("");
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadInvite = async () => {
    try {
      const { data } = await apiClient.get("/institute/invite");
      setInvite(data);
      if (data.inviteExpiresAt) {
        setExpiryDate(data.inviteExpiresAt.slice(0, 16)); // datetime-local format
      } else {
        setExpiryDate("");
      }
    } catch {
      // If 403/no permission, just show empty
    }
    setLoading(false);
  };

  useEffect(() => { loadInvite(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const payload = {};
      if (expiryDate) payload.expiresAt = new Date(expiryDate).toISOString();
      const { data } = await apiClient.post("/institute/invite/generate", payload);
      setInvite(data);
      showToast("Invite link generated!");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to generate invite link", "error");
    }
    setGenerating(false);
  };

  const handleToggle = async (enabled) => {
    setToggling(true);
    try {
      await apiClient.patch("/institute/invite/toggle", { enabled });
      setInvite((prev) => ({ ...prev, inviteEnabled: enabled }));
      showToast(enabled ? "Invite link enabled" : "Invite link disabled");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to toggle invite", "error");
    }
    setToggling(false);
  };

  const handleSaveExpiry = async () => {
    setSavingExpiry(true);
    try {
      const payload = { expiresAt: expiryDate ? new Date(expiryDate).toISOString() : null };
      await apiClient.patch("/institute/invite/expiry", payload);
      showToast("Invite expiry updated!");
      loadInvite();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to update expiry", "error");
    }
    setSavingExpiry(false);
  };

  const handleCopy = () => {
    if (invite?.inviteUrl) {
      navigator.clipboard.writeText(invite.inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return null;

  return (
    <AppCard style={{ marginTop: 16 }}>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <Link2 size={18} /> Student Invite Link
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">
          Generate a shareable link so students can self-register for your institute
        </p>
      </AppCardHeader>
      <AppCardContent>
        {!invite?.inviteCode ? (
          /* No invite code yet — show generate button */
          <div>
            <div className="settings-field invite-expiry-init">
              <label htmlFor="inviteExpiryInit">Expiry Date (optional)</label>
              <input
                id="inviteExpiryInit"
                type="datetime-local"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
            <AppButton onClick={handleGenerate} disabled={generating}>
              <Link2 size={16} /> {generating ? "Generating…" : "Generate Invite Link"}
            </AppButton>
          </div>
        ) : (
          /* Invite code exists — show management UI */
          <div>
            {/* Invite URL with copy */}
            <div className="settings-field invite-url-field">
              <label>Invite Link</label>
              <div className="invite-url-row">
                <input
                  type="text"
                  value={invite.inviteUrl || ""}
                  readOnly
                  onClick={(e) => e.target.select()}
                  className="invite-url-input"
                />
                <AppButton
                  onClick={handleCopy}
                  className="invite-copy-btn"
                  title="Copy link"
                >
                  <Copy size={14} /> {copied ? "Copied!" : "Copy"}
                </AppButton>
              </div>
            </div>

            {/* Enable/Disable toggle */}
            <div className="invite-toggle-row">
              <ToggleSwitch
                checked={!!invite.inviteEnabled}
                onChange={(val) => handleToggle(val)}
                id="inviteToggle"
              />
              <label htmlFor="inviteToggle" className="invite-toggle-label">
                {invite.inviteEnabled ? "Invite link is active" : "Invite link is disabled"}
              </label>
              {toggling && <span className="invite-saving-hint">Saving…</span>}
            </div>

            {/* Expired badge */}
            {invite.isExpired && (
              <div className="invite-expired-badge">
                This invite link has expired. Update the expiry date or remove it to re-enable.
              </div>
            )}

            {/* Expiry date */}
            <div className="invite-expiry-row">
              <div className="settings-field invite-expiry-field">
                <label htmlFor="inviteExpiry">Expiry Date</label>
                <input
                  id="inviteExpiry"
                  type="datetime-local"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
                <span className="invite-field-hint">Leave empty for no expiry</span>
              </div>
              <AppButton onClick={handleSaveExpiry} disabled={savingExpiry} className="invite-expiry-btn">
                <Save size={14} /> {savingExpiry ? "Saving…" : "Update Expiry"}
              </AppButton>
            </div>

            {/* Regenerate */}
            <Separator />
            <div className="invite-regenerate-row">
              <AppButton
                onClick={handleGenerate}
                disabled={generating}
                className="invite-regenerate-btn"
              >
                <RefreshCw size={14} /> {generating ? "Generating…" : "Regenerate Link"}
              </AppButton>
              <span className="invite-field-hint">This will invalidate the old link</span>
            </div>
          </div>
        )}
      </AppCardContent>
    </AppCard>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Notification Settings Tab
   ══════════════════════════════════════════════════════════════════════════════ */
function NotificationSettings({ settings, setSettings, onSave, saving }) {
  const toggle = (field) => (val) =>
    setSettings((s) => ({ ...s, [field]: val }));

  const notifItems = [
    {
      id: "emailNotifications",
      icon: Mail,
      label: "Email Notifications",
      desc: "Receive email notifications for important updates",
      value: settings.emailNotifications ?? true,
    },
    {
      id: "smsNotifications",
      icon: MessageSquare,
      label: "SMS Notifications",
      desc: "Receive SMS alerts for critical events",
      value: settings.smsNotifications ?? false,
    },
    {
      id: "enquiryAlerts",
      icon: Bell,
      label: "New Enquiry Alerts",
      desc: "Get notified when a new enquiry is submitted",
      value: settings.enquiryAlerts ?? true,
    },
    {
      id: "paymentReminders",
      icon: CreditCard,
      label: "Payment Reminders",
      desc: "Send automatic fee reminders to students",
      value: settings.paymentReminders ?? true,
    },
    {
      id: "batchStartNotifications",
      icon: CalendarCheck,
      label: "Batch Start Notifications",
      desc: "Notify students before batch starts",
      value: settings.batchStartNotifications ?? true,
    },
    {
      id: "whatsappNotifications",
      icon: MessageCircle,
      label: "WhatsApp Notifications",
      desc: "Send automated reminders via WhatsApp (class, fee, assignment)",
      value: settings.whatsappNotifications ?? false,
    },
  ];

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <Bell size={18} /> Notification Preferences
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">Configure how you receive notifications</p>
      </AppCardHeader>
      <AppCardContent>
        <div className="settings-notif-list">
          {notifItems.map((item, idx) => {
            const Icon = item.icon;
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && <Separator />}
                <div className="settings-notif-row">
                  <div className="settings-notif-info">
                    <div className="settings-notif-icon">
                      <Icon size={16} />
                    </div>
                    <div>
                      <p className="settings-notif-label">{item.label}</p>
                      <p className="settings-notif-desc">{item.desc}</p>
                    </div>
                  </div>
                  <ToggleSwitch
                    id={item.id}
                    checked={item.value}
                    onChange={toggle(item.id)}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <div className="settings-save-row">
          <AppButton onClick={() => onSave("notifications")} disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Branding Settings Tab
   ══════════════════════════════════════════════════════════════════════════════ */
function BrandingSettings({ settings, setSettings, logoUrl, faviconUrl, onLogoUpload, onLogoDelete, onFaviconUpload, onFaviconDelete, onSave, saving }) {
  const logoFileRef = useRef(null);
  const faviconFileRef = useRef(null);

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onLogoUpload(file);
    e.target.value = "";
  };

  const handleFaviconFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onFaviconUpload(file);
    e.target.value = "";
  };

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <Palette size={18} /> Branding
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">Customize the look and feel of your LMS</p>
      </AppCardHeader>
      <AppCardContent>
        {/* Brand Name & Subtitle */}
        <div className="settings-form-grid">
          <div className="settings-field">
            <label htmlFor="brandName">Brand Name</label>
            <input
              id="brandName"
              type="text"
              placeholder="e.g. My Academy"
              value={settings.brandName || ""}
              onChange={(e) =>
                setSettings((s) => ({ ...s, brandName: e.target.value }))
              }
              maxLength={60}
            />
            <p className="settings-logo-hint">Shown on login page, sidebar, and emails. Leave blank for "LMS Campus".</p>
          </div>

          <div className="settings-field">
            <label htmlFor="brandSubtitle">Brand Subtitle</label>
            <input
              id="brandSubtitle"
              type="text"
              placeholder="e.g. Learning Management"
              value={settings.brandSubtitle || ""}
              onChange={(e) =>
                setSettings((s) => ({ ...s, brandSubtitle: e.target.value }))
              }
              maxLength={80}
            />
            <p className="settings-logo-hint">Short tagline shown below the brand name in the sidebar.</p>
          </div>
        </div>

        <Separator />

        <div className="settings-branding-section">
          <label className="settings-label">Full Logo</label>
          <div className="settings-logo-row">
            <div className="settings-logo-preview">
              {logoUrl ? (
                <img src={logoUrl} alt="Institute logo" />
              ) : (
                <Building2 size={32} />
              )}
            </div>
            <div className="settings-logo-actions">
              <input
                ref={logoFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                onChange={handleLogoFileChange}
              />
              <AppButton
                variant="outline"
                size="sm"
                onClick={() => logoFileRef.current?.click()}
              >
                <Upload size={14} /> Upload Logo
              </AppButton>
              {logoUrl && (
                <AppButton
                  variant="outline"
                  size="sm"
                  className="settings-logo-delete-btn"
                  onClick={onLogoDelete}
                >
                  <Trash2 size={14} /> Remove
                </AppButton>
              )}
              <p className="settings-logo-hint">
                Used in emails, sidebar, and desktop login. Recommended: wide banner, PNG or JPG.
              </p>
            </div>
          </div>
        </div>

        <Separator />

        <div className="settings-branding-section">
          <label className="settings-label">Favicon / App Icon</label>
          <div className="settings-logo-row">
            <div className="settings-logo-preview settings-favicon-preview">
              {faviconUrl ? (
                <img src={faviconUrl} alt="Institute favicon" />
              ) : (
                <Building2 size={24} />
              )}
            </div>
            <div className="settings-logo-actions">
              <input
                ref={faviconFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/x-icon,image/svg+xml"
                style={{ display: "none" }}
                onChange={handleFaviconFileChange}
              />
              <AppButton
                variant="outline"
                size="sm"
                onClick={() => faviconFileRef.current?.click()}
              >
                <Upload size={14} /> Upload Favicon
              </AppButton>
              {faviconUrl && (
                <AppButton
                  variant="outline"
                  size="sm"
                  className="settings-logo-delete-btn"
                  onClick={onFaviconDelete}
                >
                  <Trash2 size={14} /> Remove
                </AppButton>
              )}
              <p className="settings-logo-hint">
                Small square icon for browser tab and mobile. Recommended: 64×64px or 128×128px, PNG or ICO.
              </p>
            </div>
          </div>
        </div>

        <Separator />

        <div className="settings-branding-section">
          <label className="settings-label" htmlFor="primaryColor">Primary Color</label>
          <div className="settings-color-row">
            <input
              type="color"
              id="primaryColor"
              value={settings.primaryColor || "#2563eb"}
              onChange={(e) =>
                setSettings((s) => ({ ...s, primaryColor: e.target.value }))
              }
              className="settings-color-picker"
            />
            <input
              type="text"
              value={settings.primaryColor || "#2563eb"}
              onChange={(e) =>
                setSettings((s) => ({ ...s, primaryColor: e.target.value }))
              }
              className="settings-color-text"
              maxLength={7}
            />
          </div>
        </div>

        <Separator />

        <div className="settings-branding-section">
          <label className="settings-label" htmlFor="logoBgColor">Logo Background Color</label>
          <p className="settings-logo-hint" style={{ marginTop: 0 }}>Background color behind the logo icon in the sidebar. Match it to your logo for a polished look.</p>
          <div className="settings-color-row">
            <input
              type="color"
              id="logoBgColor"
              value={settings.logoBgColor || "#3b82f6"}
              onChange={(e) =>
                setSettings((s) => ({ ...s, logoBgColor: e.target.value }))
              }
              className="settings-color-picker"
            />
            <input
              type="text"
              value={settings.logoBgColor || "#3b82f6"}
              onChange={(e) =>
                setSettings((s) => ({ ...s, logoBgColor: e.target.value }))
              }
              className="settings-color-text"
              maxLength={7}
            />
          </div>
        </div>

        <Separator />

        {/* ── Email Branding ── */}
        <div className="settings-branding-section">
          <label className="settings-label">Email Branding</label>
          <p className="settings-logo-hint" style={{ marginTop: 0 }}>
            Control how your institute appears in outgoing emails to students and instructors.
          </p>

          <div className="d-flex align-items-center gap-3 mb-3">
            <Form.Check
              type="switch"
              id="emailBrandingEnabled"
              label="Use institute branding in emails"
              checked={settings.emailBranding?.enabled ?? true}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  emailBranding: { ...s.emailBranding, enabled: e.target.checked },
                }))
              }
            />
          </div>

          {(settings.emailBranding?.enabled ?? true) && (
            <>
              <div className="mb-3">
                <label className="settings-label" htmlFor="emailLogoType">Logo in Emails</label>
                <Form.Select
                  size="sm"
                  id="emailLogoType"
                  value={settings.emailBranding?.logoType || "full"}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      emailBranding: { ...s.emailBranding, logoType: e.target.value },
                    }))
                  }
                  style={{ maxWidth: 280 }}
                >
                  <option value="full">Full Logo</option>
                  <option value="icon">Icon Only (small)</option>
                  <option value="none">No Logo</option>
                </Form.Select>
              </div>

              <div className="mb-3">
                <label className="settings-label" htmlFor="customSenderName">Custom Sender Name</label>
                <p className="settings-logo-hint" style={{ marginTop: 0 }}>
                  Override the &quot;From&quot; display name in emails. Leave blank to use your institute name.
                </p>
                <Form.Control
                  size="sm"
                  id="customSenderName"
                  value={settings.emailBranding?.customSenderName || ""}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      emailBranding: { ...s.emailBranding, customSenderName: e.target.value },
                    }))
                  }
                  placeholder={settings.brandName || settings.instituteName || "Institute Name"}
                  maxLength={100}
                  style={{ maxWidth: 350 }}
                />
              </div>
            </>
          )}
        </div>

        <div className="settings-save-row">
          <AppButton onClick={() => onSave("branding")} disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   AI Settings Tab
   ══════════════════════════════════════════════════════════════════════════════ */
function AISettingsTab({ showToast }) {
  const [aiSettings, setAiSettings] = useState({
    aiEnabled: false,
    aiAutoExplainEnabled: false,
    aiMonthlyTokenLimit: 500000,
  });
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [settingsRes, usageRes] = await Promise.allSettled([
          getAISettings(),
          getAIUsage(),
        ]);
        if (settingsRes.status === "fulfilled") {
          const d = settingsRes.value.data?.data || settingsRes.value.data;
          setAiSettings({
            aiEnabled: d.aiEnabled ?? false,
            aiAutoExplainEnabled: d.aiAutoExplainEnabled ?? false,
            aiMonthlyTokenLimit: d.aiMonthlyTokenLimit ?? 500000,
          });
        }
        if (usageRes.status === "fulfilled") {
          setUsage(usageRes.value.data?.data || usageRes.value.data);
        }
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAISettings(aiSettings);
      showToast("AI settings saved!");
    } catch {
      showToast("Failed to save AI settings", "error");
    }
    setSaving(false);
  };

  if (loading) return <div className="settings-loading">Loading AI settings…</div>;

  const tokenUsed = usage?.totalTokens ?? 0;
  const tokenLimit = aiSettings.aiMonthlyTokenLimit || 500000;
  const usagePct = tokenLimit > 0 ? Math.min(100, Math.round((tokenUsed / tokenLimit) * 100)) : 0;

  return (
    <>
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Sparkles size={18} /> AI Document Explanations
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Automatically generate AI-powered explanations for course documents (PDF, PPT, DOCX, text)
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="settings-notif-list">
            {/* Enable AI */}
            <div className="settings-notif-row">
              <div className="settings-notif-info">
                <div className="settings-notif-icon"><Sparkles size={16} /></div>
                <div>
                  <div className="settings-notif-label">Enable AI Explanations</div>
                  <div className="settings-notif-desc">Allow AI explanation generation for document lessons</div>
                </div>
              </div>
              <ToggleSwitch
                id="aiEnabled"
                checked={aiSettings.aiEnabled}
                onChange={(val) => setAiSettings(s => ({ ...s, aiEnabled: val }))}
              />
            </div>

            <Separator />

            {/* Auto-explain */}
            <div className="settings-notif-row">
              <div className="settings-notif-info">
                <div className="settings-notif-icon"><Zap size={16} /></div>
                <div>
                  <div className="settings-notif-label">Auto-Generate on Upload</div>
                  <div className="settings-notif-desc">Automatically queue AI explanations when a document lesson is confirmed</div>
                </div>
              </div>
              <ToggleSwitch
                id="aiAutoExplain"
                checked={aiSettings.aiAutoExplainEnabled}
                onChange={(val) => setAiSettings(s => ({ ...s, aiAutoExplainEnabled: val }))}
              />
            </div>

            <Separator />

            {/* Monthly token limit */}
            <div className="settings-ai-field">
              <label htmlFor="aiTokenLimit">Monthly Token Limit</label>
              <div className="settings-ai-field__row">
                <input
                  id="aiTokenLimit"
                  type="number"
                  min={10000}
                  step={10000}
                  value={aiSettings.aiMonthlyTokenLimit}
                  onChange={(e) => setAiSettings(s => ({ ...s, aiMonthlyTokenLimit: Number(e.target.value) || 500000 }))}
                  className="settings-ai-field__input"
                />
                <span className="settings-ai-field__hint">tokens/month</span>
              </div>
            </div>
          </div>

          <div className="settings-save-row">
            <AppButton onClick={handleSave} disabled={saving}>
              <Save size={16} /> {saving ? "Saving…" : "Save AI Settings"}
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>

      {/* Usage card */}
      {usage && (
        <AppCard style={{ marginTop: 16 }}>
          <AppCardHeader>
            <AppCardTitle>
              <span className="settings-card-title">
                <Zap size={18} /> AI Usage This Month
              </span>
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <div className="settings-ai-usage">
              <div className="settings-ai-usage__bar-wrap">
                <div className="settings-ai-usage__bar">
                  <div
                    className={`settings-ai-usage__bar-fill ${usagePct > 90 ? 'settings-ai-usage__bar-fill--danger' : ''}`}
                    style={{ width: `${usagePct}%` }}
                  />
                </div>
                <span className="settings-ai-usage__pct">{usagePct}%</span>
              </div>
              <div className="settings-ai-usage__stats">
                <div>
                  <span className="settings-ai-usage__label">Tokens Used</span>
                  <span className="settings-ai-usage__value">{tokenUsed.toLocaleString()}</span>
                </div>
                <div>
                  <span className="settings-ai-usage__label">Limit</span>
                  <span className="settings-ai-usage__value">{tokenLimit.toLocaleString()}</span>
                </div>
                <div>
                  <span className="settings-ai-usage__label">Lessons Processed</span>
                  <span className="settings-ai-usage__value">{usage?.totalLessons ?? 0}</span>
                </div>
                <div>
                  <span className="settings-ai-usage__label">Est. Cost</span>
                  <span className="settings-ai-usage__value">${(usage?.totalCost ?? 0).toFixed(4)}</span>
                </div>
              </div>
            </div>
          </AppCardContent>
        </AppCard>
      )}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Modules Settings Tab — institute admin toggles module visibility for users
   ══════════════════════════════════════════════════════════════════════════════ */

const MODULE_DEFS = [
  { key: 'multiBranch',       label: 'Multi-Branch',        desc: 'Manage multiple branches with branch-wise batches, enquiries, and staff assignment', planKey: 'multiBranch' },
  { key: 'selfPacedCourses',  label: 'Self-Paced Learning',  desc: 'Allow students to browse and enrol in self-paced courses',     planKey: 'selfPacedCourses' },
  { key: 'codingPractice',    label: 'Coding Practice',      desc: 'In-browser coding environment for student practice',          planKey: 'codingPractice' },
  { key: 'aiMock',            label: 'AI Mock Interviews',   desc: 'AI-driven mock interviews with real-time feedback',            planKey: 'aiMock' },
  { key: 'aiLearning',        label: 'AI Learning',          desc: 'Personalised AI tutor for student self-study',                 planKey: 'aiLearning' },
  { key: 'aiQuestionBank',    label: 'AI Question Bank',     desc: 'AI-generated question banks and quiz creation',                planKey: 'aiQuestionBank' },
  { key: 'aiDoubtSolving',    label: 'AIvo – AI Doubt Assistant', desc: 'AI-powered doubt solving chatbot for students',           planKey: 'aiDoubtSolving' },
  { key: 'interviews',        label: 'Interviews',           desc: 'Schedule and manage interview sessions with candidates',       planKey: 'interviews' },
  { key: 'assignments',       label: 'Assignments',          desc: 'Create and manage assignments, grading, and submissions',      planKey: 'assignments' },
  { key: 'feeManagement',     label: 'Fee Management',       desc: 'Fee collection, payment tracking, invoices, and discounts',    planKey: 'feeManagement' },
  { key: 'enquiryManagement', label: 'Enquiry Management',   desc: 'Lead capture, follow-up tracking, and conversion workflows',   planKey: 'enquiryManagement' },
  { key: 'offlineAttendance', label: 'Offline Attendance',   desc: 'Allow instructors to manually mark attendance for students who attended class offline', planKey: null },
];

function ModulesSettingsTab({ showToast }) {
  const { planAllowsFeature, refreshPlan } = usePlan();
  const [modules, setModules] = useState({});
  const [allowPlatformCodingQuestions, setAllowPlatformCodingQuestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get('/institute/settings');
        const em = data.settings?.enabledModules || {};
        // Default each key to true if not explicitly set
        const init = {};
        MODULE_DEFS.forEach(m => { init[m.key] = em[m.key] !== false; });
        setModules(init);
        setAllowPlatformCodingQuestions(!!data.settings?.allowPlatformCodingQuestions);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleToggle = (key, val) => {
    setModules(prev => ({ ...prev, [key]: val }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/institute/settings', {
        enabledModules: modules,
        allowPlatformCodingQuestions,
      });
      showToast('Module visibility settings saved!');
      refreshPlan(); // refresh sidebar immediately
    } catch {
      showToast('Failed to save module settings', 'error');
    }
    setSaving(false);
  };

  if (loading) return <div className="settings-loading">Loading module settings…</div>;

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <ToggleRight size={18} /> Module Visibility
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">
          Control which modules are visible to your students and staff.
          Disabled modules will be hidden from the sidebar and all users.
          Modules not included in your plan are locked and cannot be enabled.
        </p>
      </AppCardHeader>
      <AppCardContent>
        <div className="settings-notif-list">
          {MODULE_DEFS.map((mod, idx) => {
            const planAllows = mod.planKey ? planAllowsFeature(mod.planKey) : true;
            const codingPracticeOn = planAllowsFeature('codingPractice') && modules.codingPractice !== false;
            return (
              <React.Fragment key={mod.key}>
                {idx > 0 && <Separator />}
                <div className={`settings-notif-row ${!planAllows ? 'settings-notif-row--disabled' : ''}`}>
                  <div className="settings-notif-info">
                    <div>
                      <div className="settings-notif-label">
                        {mod.label}
                        {!planAllows && (
                          <span className="settings-module-badge">Not in plan</span>
                        )}
                      </div>
                      <div className="settings-notif-desc">{mod.desc}</div>
                    </div>
                  </div>
                  <ToggleSwitch
                    id={`mod-${mod.key}`}
                    checked={planAllows ? modules[mod.key] : false}
                    onChange={(val) => handleToggle(mod.key, val)}
                    disabled={!planAllows}
                  />
                </div>
                {mod.key === 'codingPractice' && (
                  <div className={`settings-notif-row settings-notif-row--child ${!codingPracticeOn ? 'settings-notif-row--disabled' : ''}`}>
                    <div className="settings-notif-info">
                      <div>
                        <div className="settings-notif-label">Allow Platform Coding Questions</div>
                        <div className="settings-notif-desc">
                          Show LMS Campus' curated public coding questions to your students in Coding Practice, in addition to questions you author.
                        </div>
                      </div>
                    </div>
                    <ToggleSwitch
                      id="allowPlatformCodingQuestions"
                      checked={codingPracticeOn ? allowPlatformCodingQuestions : false}
                      onChange={setAllowPlatformCodingQuestions}
                      disabled={!codingPracticeOn}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className="settings-save-row">
          <AppButton onClick={handleSave} disabled={saving}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save Module Settings'}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Video Security Settings — global toggle for desktop app requirement
   ══════════════════════════════════════════════════════════════════════════════ */
function VideoSecuritySettingsTab({ showToast }) {
  const { hasPlanFeature } = usePlan();
  const planAllows = hasPlanFeature('secureVideoPlayer');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get('/institute/settings');
        setEnabled(!!data.settings?.videoSecurityEnabled);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/institute/settings', { videoSecurityEnabled: enabled });
      showToast('Video security settings saved!');
    } catch {
      showToast('Failed to save video security settings', 'error');
    }
    setSaving(false);
  };

  if (loading) return <div className="settings-loading">Loading video security settings…</div>;

  return (
    <AppCard style={{ marginTop: 16 }}>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <Lock size={18} /> Video Security (Desktop App)
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">
          When enabled, students must use the LMS Campus Desktop App to watch videos in courses that have video security turned on.
          This provides OS-level screen capture protection — preventing tools like OBS, screen recorders, and screenshot extensions.
          Download prevention and watermarks are always active for all customers regardless of this setting.
        </p>
      </AppCardHeader>
      <AppCardContent>
        <div className="settings-notif-list">
          <div className={`settings-notif-row ${!planAllows ? 'settings-notif-row--disabled' : ''}`}>
            <div className="settings-notif-info">
              <div>
                <div className="settings-notif-label">
                  Enable Video Security
                  {!planAllows && (
                    <span className="settings-module-badge">Not in plan</span>
                  )}
                </div>
                <div className="settings-notif-desc">
                  Globally enable video security for your institute. You can then turn it on per-course in course settings.
                </div>
              </div>
            </div>
            <ToggleSwitch
              id="videoSecurityEnabled"
              checked={planAllows ? enabled : false}
              onChange={setEnabled}
              disabled={!planAllows}
            />
          </div>
        </div>
        <div className="settings-save-row">
          <AppButton onClick={handleSave} disabled={saving || !planAllows}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save Video Security Settings'}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Main Settings Page
   ══════════════════════════════════════════════════════════════════════════════ */
const SettingsPage = () => {
  const { refreshBranding } = useBranding();
  const { hasPlanFeature } = usePlan();
  const [settings, setSettings] = useState({});
  const [institute, setInstitute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Fetch settings + logo on mount
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [settingsRes, logoRes, faviconRes] = await Promise.all([
          apiClient.get("/institute/settings"),
          apiClient.get("/institute/settings/logo-url"),
          apiClient.get("/institute/settings/favicon-url"),
        ]);
        setSettings(settingsRes.data.settings || {});
        if (settingsRes.data.institute) setInstitute(settingsRes.data.institute);
        setLogoUrl(logoRes.data.url || null);
        setFaviconUrl(faviconRes.data.url || null);
      } catch (err) {
        // Silently handle 403 (permission denied) — tabs will show locked state
        const status = err?.response?.status;
        if (status !== 403) {
          showToast("Failed to load settings", "error");
        }
      }
      setLoading(false);
    };
    load();
  }, []);

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  // Save handler — sends all settings fields
  const handleSave = async (section) => {
    setSaving(true);
    try {
      await apiClient.put("/institute/settings", settings);
      showToast(`${capitalize(section)} settings saved successfully!`);
      // If branding was saved, refresh the global BrandingContext so sidebar/nav update
      if (section === "branding") refreshBranding();
    } catch {
      showToast("Failed to save settings", "error");
    }
    setSaving(false);
  };

  // Logo upload via server-side upload
  const handleLogoUpload = async (file) => {
    try {
      showToast("Uploading logo…", "info");
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post("/institute/settings/logo-upload-url", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const logoRes = await apiClient.get("/institute/settings/logo-url");
      setLogoUrl(logoRes.data.url || null);
      showToast("Logo uploaded successfully!");
      refreshBranding();
    } catch {
      showToast("Failed to upload logo", "error");
    }
  };

  const handleLogoDelete = async () => {
    try {
      await apiClient.delete("/institute/settings/logo");
      setLogoUrl(null);
      showToast("Logo removed");
      refreshBranding();
    } catch {
      showToast("Failed to remove logo", "error");
    }
  };

  // Favicon upload via server-side upload
  const handleFaviconUpload = async (file) => {
    try {
      showToast("Uploading favicon…", "info");
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post("/institute/settings/favicon-upload-url", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const faviconRes = await apiClient.get("/institute/settings/favicon-url");
      setFaviconUrl(faviconRes.data.url || null);
      showToast("Favicon uploaded successfully!");
      refreshBranding();
    } catch {
      showToast("Failed to upload favicon", "error");
    }
  };

  const handleFaviconDelete = async () => {
    try {
      await apiClient.delete("/institute/settings/favicon");
      setFaviconUrl(null);
      showToast("Favicon removed");
      refreshBranding();
    } catch {
      showToast("Failed to remove favicon", "error");
    }
  };

  /* All 5 settings tabs — every admin sees them, but locked tabs show a
     "no permission" message instead of the actual content. */
  /* Each tab may have a `planFeature` — if the plan doesn't include the
     feature the tab is hidden entirely (not just locked). */
  const allTabs = [
    { value: "general",       label: "General",       permission: "settings_general" },
    { value: "modules",       label: "Modules",       permission: "settings_general" },
    { value: "notifications", label: "Notifications", permission: "settings_notifications" },
    { value: "branding",      label: "Branding",      permission: "settings_branding",  planFeature: "customBranding" },
    { value: "payments",      label: "Payments",      permission: "settings_payments",  planFeature: "feeManagement" },
    { value: "zoom",          label: "Zoom",          permission: "settings_zoom",      planFeature: "zoomV2" },
    { value: "api",            label: "Public API",    permission: "settings_general" },
    { value: "zoho",           label: "Zoho CRM",     permission: "settings_general",  planFeature: "zohoCrm" },
    { value: "lead-sources",  label: "Lead Sources",  permission: "settings_general" },
    { value: "ai",            label: "AI",            permission: "settings_general" },
  ];

  // Only show tabs whose plan feature is available (or tabs without a planFeature)
  const visibleTabs = allTabs.filter(t => !t.planFeature || hasPlanFeature(t.planFeature));

  // Pick the first unlocked tab as default; fall back to first visible tab
  const firstUnlocked = visibleTabs.find(t => hasPermission(t.permission));
  const defaultTab = firstUnlocked ? firstUnlocked.value : (visibleTabs[0]?.value || "general");

  if (loading) {
    return <div className="settings-loading">Loading settings…</div>;
  }

  /* Locked-tab placeholder shown inside tabs the admin cannot access */
  const LockedTabContent = ({ label }) => (
    <div className="settings-locked">
      <div className="settings-locked__icon">
        <Lock size={32} />
      </div>
      <h3 className="settings-locked__title">Access Restricted</h3>
      <p className="settings-locked__desc">
        You do not have permission to view the <strong>{label}</strong> section.
        Contact your Super Admin to request access.
      </p>
    </div>
  );

  return (
    <div className="settings-page">
      <PageHeader
        icon={SettingsIcon}
        title="Settings"
        description="Manage your institute settings and preferences"
      />

      <Tabs defaultValue={defaultTab} className="settings-tabs">
        <TabsList>
          {visibleTabs.map(t => {
            const locked = !hasPermission(t.permission);
            return (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className={locked ? "settings-tab--locked" : ""}
              >
                {t.label}
                {locked && <Lock size={12} className="settings-tab-lock-icon" />}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Render tab content only for visible (plan‑included) tabs */}
        {visibleTabs.some(t => t.value === "general") && (
          <TabsContent value="general">
            {hasPermission("settings_general") ? (
              <>
                <GeneralSettings
                  settings={settings}
                  setSettings={setSettings}
                  onSave={handleSave}
                  saving={saving}
                  institute={institute}
                />
                <InviteSettings showToast={showToast} />
              </>
            ) : (
              <LockedTabContent label="General" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "modules") && (
          <TabsContent value="modules">
            {hasPermission("settings_general") ? (
              <>
                <ModulesSettingsTab showToast={showToast} />
                <VideoSecuritySettingsTab showToast={showToast} />
              </>
            ) : (
              <LockedTabContent label="Modules" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "notifications") && (
          <TabsContent value="notifications">
            {hasPermission("settings_notifications") ? (
              <NotificationSettings
                settings={settings}
                setSettings={setSettings}
                onSave={handleSave}
                saving={saving}
              />
            ) : (
              <LockedTabContent label="Notifications" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "branding") && (
          <TabsContent value="branding">
            {hasPermission("settings_branding") ? (
              <BrandingSettings
                settings={settings}
                setSettings={setSettings}
                logoUrl={logoUrl}
                faviconUrl={faviconUrl}
                onLogoUpload={handleLogoUpload}
                onLogoDelete={handleLogoDelete}
                onFaviconUpload={handleFaviconUpload}
                onFaviconDelete={handleFaviconDelete}
                onSave={handleSave}
                saving={saving}
              />
            ) : (
              <LockedTabContent label="Branding" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "payments") && (
          <TabsContent value="payments">
            {hasPermission("settings_payments") ? (
              <>
                <RazorpaySettings />
                <BankDetailsSettings />
              </>
            ) : (
              <LockedTabContent label="Payments" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "zoom") && (
          <TabsContent value="zoom">
            {hasPermission("settings_zoom") ? (
              <>
                <MeetingProviderSelector />
                <ZoomSettings />
                <ZohoMeetingSettings />
              </>
            ) : (
              <LockedTabContent label="Live Class" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "api") && (
          <TabsContent value="api">
            {hasPermission("settings_general") ? (
              <PublicApiSettings />
            ) : (
              <LockedTabContent label="Public API" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "zoho") && (
          <TabsContent value="zoho">
            {hasPermission("settings_general") ? (
              <ZohoSettings />
            ) : (
              <LockedTabContent label="Zoho CRM" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "lead-sources") && (
          <TabsContent value="lead-sources">
            {hasPermission("settings_general") ? (
              <LeadSourcesSettings />
            ) : (
              <LockedTabContent label="Lead Sources" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "ai") && (
          <TabsContent value="ai">
            {hasPermission("settings_general") ? (
              <AISettingsTab showToast={showToast} />
            ) : (
              <LockedTabContent label="AI" />
            )}
          </TabsContent>
        )}
      </Tabs>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default SettingsPage;
